/**
 * Telemetry integration helpers for player events.
 * Provides clean integration with the telemetry service.
 */

/** Telemetry handler for tracking player events. */
export type TelemetryHandler = {
  trackVideoStart: (assetId: string, metadata?: Record<string, unknown>) => void;
  trackVideoPause: (assetId: string, position: number) => void;
  trackVideoResume: (assetId: string, position: number) => void;
  trackVideoSeek: (assetId: string, fromPosition: number, toPosition: number) => void;
  trackVideoComplete: (assetId: string, duration: number) => void;
  trackVideoAbandon: (assetId: string, position: number, duration: number) => void;
  trackPlayback: (action: string, assetId: string, metadata?: Record<string, unknown>) => void;
};

/** Context for managing telemetry state */
export type TelemetryContext = {
  handler?: TelemetryHandler;
  assetId?: string;
  hasStarted: boolean;
  lastPosition: number;
  startTime?: number;
};

/** Initialize telemetry context */
export function createTelemetryContext(): TelemetryContext {
  return {
    hasStarted: false,
    lastPosition: 0,
  };
}

/** Track video start event */
export function trackVideoStart(ctx: TelemetryContext, assetId: string, metadata?: Record<string, unknown>): void {
  if (!ctx.handler || ctx.hasStarted) return;

  ctx.assetId = assetId;
  ctx.hasStarted = true;
  ctx.startTime = Date.now();
  ctx.handler.trackVideoStart(assetId, metadata);
}

/** Track video pause event */
export function trackVideoPause(ctx: TelemetryContext, position: number): void {
  if (!ctx.handler || !ctx.assetId) return;

  ctx.lastPosition = position;
  ctx.handler.trackVideoPause(ctx.assetId, position);
}

/** Track video resume event */
export function trackVideoResume(ctx: TelemetryContext, position: number): void {
  if (!ctx.handler || !ctx.assetId) return;

  ctx.lastPosition = position;
  ctx.handler.trackVideoResume(ctx.assetId, position);
}

/** Track video seek event */
export function trackVideoSeek(ctx: TelemetryContext, fromPosition: number, toPosition: number): void {
  if (!ctx.handler || !ctx.assetId) return;

  ctx.lastPosition = toPosition;
  ctx.handler.trackVideoSeek(ctx.assetId, fromPosition, toPosition);
}

/** Track video completion */
export function trackVideoComplete(ctx: TelemetryContext, duration: number): void {
  if (!ctx.handler || !ctx.assetId) return;

  ctx.handler.trackVideoComplete(ctx.assetId, duration);
}

/** Track video abandon (when switching away or closing) */
export function trackVideoAbandon(ctx: TelemetryContext, duration: number): void {
  if (!ctx.handler || !ctx.assetId || !ctx.hasStarted) return;

  ctx.handler.trackVideoAbandon(ctx.assetId, ctx.lastPosition, duration);
}

/** Generic playback event tracking */
export function trackPlaybackEvent(ctx: TelemetryContext, action: string, metadata?: Record<string, unknown>): void {
  if (!ctx.handler || !ctx.assetId) return;

  ctx.handler.trackPlayback(action, ctx.assetId, metadata);
}
